import { FilterProps, FilterValues, SupportedKmsTypeFilter } from "@kaltura/mediaspace-shared-types";
import { useEffect, useState } from "react";
import { extractDefaultFilterValues } from "@kaltura/mediaspace-shared-utils";
import { translate } from "@kaltura/ds-react-utils";
import { ConfigProvider, useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import styled from "@emotion/styled";
import { FilterBarButtonWithModal, FilterMenuProps, SearchBar } from "@kaltura/mediaspace-shared-search";
import { isEqual } from "lodash";

const StyledSearchFormContainer = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

export interface MediasSearchBarProps {
    /** filers definitions*/
    filters: FilterProps<SupportedKmsTypeFilter>[];

    /** props passed down to the search bar's filter menu*/
    filterMenuProps: FilterMenuProps;

    /** callback invoked when the filters change */
    onFilterChange: (values: FilterValues) => void;

    /** callback invoked when the filters are cleared */
    onFiltersCleared: () => void;
    showSearchInput: boolean;
}

export const MediaSearchBar = ({
    filters,
    filterMenuProps,
    onFilterChange,
    onFiltersCleared,
    showSearchInput,
}: MediasSearchBarProps) => {
    const [isInitialFilterValues, setIsInitialFilterValues] = useState(true);
    const defaultFilterValues = extractDefaultFilterValues(filters);
    const [filterValues, setFilterValues] = useState<FilterValues>(defaultFilterValues);

    // handle filter change  - triggered when search in filed setting is changed
    const handleFilterChange = (values: FilterValues) => {
        setIsInitialFilterValues(false);
        setFilterValues(values);
    };

    const searchBarButtons = [
        <FilterBarButtonWithModal filters={filters} values={filterValues} onChange={handleFilterChange} />,
    ];

    // handle submit search form
    const handleKeywordSearchSubmit = (value: string) => {
        setIsInitialFilterValues(false);
        setFilterValues((filterValues) => {
            if (value) {
                return { ...filterValues, keyword: value };
            }
            else {
                const { keyword, ...rest } = filterValues;
                return rest;
            }
        });
    };

    // handle filter menu change (triggered when filter value changed in the filters modal)
    const handleFilterMenuChange = (values: FilterValues) => {
        setIsInitialFilterValues(false);
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
    };

    useEffect(() => {
        if (!isInitialFilterValues) {
            //if the keyword is not empty, always invoke the callback
            if (filterValues["keyword"]) {
                onFilterChange(filterValues);
                return;
            }
            const replaceEmptyWithDefaultValues = (filterValues: FilterValues): FilterValues => {
                const updatedValues: FilterValues = { ...filterValues };

                for (const key in filterValues) {
                    if (
                        filterValues[key] === null
                        || filterValues[key] === ""
                        || (Array.isArray(filterValues[key]) && filterValues[key].length === 0)
                    ) {
                        updatedValues[key] = defaultFilterValues[key];
                    }
                }

                return updatedValues;
            };

            const { fields, ...normalizedFilters } = replaceEmptyWithDefaultValues(filterValues);
            const isFilterValuesResetToInitial = isEqual(normalizedFilters, defaultFilterValues);
            if (!isFilterValuesResetToInitial) {
                onFilterChange(filterValues);
            }
            else {
                onFiltersCleared();
            }
        }
    }, [filterValues, onFilterChange, isInitialFilterValues]);

    const config = useConfig();
    const mediaSearchConfig = {
        ...config,
        analytics: { ...config.analytics, pageType: "Channel - Videos tab" },
    };

    return (
        <ConfigProvider context={mediaSearchConfig}>
            {filterValues && filterMenuProps && (
                <StyledSearchFormContainer>
                    {showSearchInput && (
                        <SearchBar
                            buttons={searchBarButtons}
                            keywordSearchProps={{
                                onSubmit: handleKeywordSearchSubmit,
                                value: filterValues["keyword"] as string,
                                placeholder: translate("Search in Media"),
                                ariaLabel: translate("Search in Media"),
                            }}
                            filterMenuProps={{
                                ...filterMenuProps,
                                values: filterValues,
                                onChange: handleFilterMenuChange,
                            }}
                        />
                    )}
                </StyledSearchFormContainer>
            )}
        </ConfigProvider>
    );
};
